import { t } from '@lingui/macro';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Message from '@decisiv/ui-components/lib/components/Message';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import Download from '@decisiv/iconix/lib/components/Download';

import { type PolledDepotReset } from '../hooks/usePollDepotReset';
import { buildDepotSummaryCounts } from '../utils/depotSummaryCounts';

interface SummaryStepProps {
  depotReset: PolledDepotReset;
  /**
   * The poll gave up on a run that had ALREADY reported a status, so its real
   * outcome is unknown -- it may still be applying server-side. Reuses the
   * FAILURE variant (no success to claim) but not FAILED's "not applied"
   * copy, which would misrepresent a destructive operation in progress.
   */
  outcomeUnknown?: boolean;
}

export default function SummaryStep({
  depotReset,
  outcomeUnknown = false,
}: SummaryStepProps) {
  const { status, outcome, errorMessage, reportUrl, depotSummaries } =
    depotReset;

  const { depotCount, succeeded, withErrors } =
    buildDepotSummaryCounts(depotSummaries);

  // For a SUCCESS outcome every actionable item completed, so the plan's
  // counts are also the applied counts. noopCount is excluded: nothing
  // changed, so it is not part of what was reconciled.
  const reconciled =
    depotReset.addCount +
    depotReset.transferCount +
    depotReset.updateCount +
    depotReset.releaseCount;

  // A FAILED run never applied the file at all, so the generic/explicit
  // "not applied" copy is accurate. A COMPLETED run with a FAILURE outcome
  // *did* apply the file and every item still failed, so "not applied"
  // would be a lie — that case gets its own "download the report" copy. And
  // when we merely lost track of a live run, BOTH of those would be lies: we
  // know nothing about what was applied, so we say exactly that.
  let failureBody: string;
  if (outcomeUnknown) {
    failureBody =
      errorMessage ??
      t`We stopped receiving updates for this reset, so its final state is unknown and some changes may already have been applied. Reload the page to check on it before uploading another file.`;
  } else if (status === 'COMPLETED') {
    failureBody = t`No changes could be applied. Download the report to see why each row failed.`;
  } else {
    failureBody =
      errorMessage ??
      t`The uploaded file was not applied and no asset additions, transfers, or removals were performed.`;
  }

  return (
    <Flex flexDirection="column">
      <H3>{t`Summary`}</H3>

      <Flex
        marginTop={1.5}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Flex flexDirection="column" flex={1}>
          {outcome === 'SUCCESS' && (
            <>
              <Message intent="success" size="small">
                {t`Depots successfully reset`}
              </Message>
              <P size="small" shade={1} marginTop={0.5}>
                {t`${reconciled} assets reconciled across ${depotCount} depots.`}
              </P>
            </>
          )}

          {outcome === 'PARTIAL_SUCCESS' && (
            <>
              <P size="small" weight="semibold">
                {t`Depots partially reset`}
              </P>
              <P size="small" shade={1} marginTop={0.5}>
                {t`Some changes across ${depotCount} depots could not be applied.`}
              </P>
              <Flex marginTop={1}>
                <Message intent="success" size="small">
                  {t`${succeeded} depots successfully reset`}
                </Message>
              </Flex>
              <Flex marginTop={0.5}>
                <Message intent="warning" size="small">
                  {t`${withErrors} depots with errors`}
                </Message>
              </Flex>
            </>
          )}

          {(outcome === 'FAILURE' || outcomeUnknown) && (
            <>
              <Message intent="danger" size="small">
                {outcomeUnknown
                  ? t`We lost track of the depot(s) reset.`
                  : t`The depot(s) reset could not be completed.`}
              </Message>
              <P size="small" shade={1} marginTop={0.5}>
                {failureBody}
              </P>
            </>
          )}
        </Flex>

        {/* Single condition for all three variants: reportUrl is null until
            COMPLETED, so a run that never applied has nothing to download. */}
        {reportUrl != null && (
          <Button
            text={t`Download Report`}
            icon={Download}
            size="small"
            kind="secondary"
            onClick={() => window.location.assign(reportUrl)}
          />
        )}
      </Flex>
    </Flex>
  );
}
